import { firestore } from '../../../../../../libs/firebase-service/src';

// Utility function to convert store time to user's local time zone
export const convertStoreTimeToUserLocal = (
  storeTime,
  storeTimezone,
  includeTimezoneInfo = false
) => {
  try {
    // Get user's timezone
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    // Determine whether a timezone conversion will be necessary
    const convertedFlag = (storeTimezone || 'UTC') !== userTimezone;

    // Parse the store time
    const [time, period] =
      storeTime.includes('AM') || storeTime.includes('PM')
        ? storeTime.split(/\s+/)
        : [storeTime, null];

    const [hours, minutes] = time.split(':').map((num) => parseInt(num));
    let adjustedHours = hours;

    // Handle 12-hour format
    if (period) {
      if (period.toUpperCase() === 'PM' && hours !== 12) {
        adjustedHours = hours + 12;
      } else if (period.toUpperCase() === 'AM' && hours === 12) {
        adjustedHours = 0;
      }
    }

    // SIMPLE APPROACH: Create a date representing today at the store time, then convert
    const today = new Date();

    // Create date object representing the store time TODAY
    // We'll create it as if it's in UTC first, then adjust
    const storeDateTime = new Date();
    storeDateTime.setFullYear(today.getFullYear());
    storeDateTime.setMonth(today.getMonth());
    storeDateTime.setDate(today.getDate());
    storeDateTime.setHours(adjustedHours, minutes || 0, 0, 0);

    const now = new Date();

    // Get the current time as it appears in store timezone
    const storeNow = new Intl.DateTimeFormat('en-CA', {
      timeZone: storeTimezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }).format(now);

    // Get the current time as it appears in user timezone
    const userNow = new Intl.DateTimeFormat('en-CA', {
      timeZone: userTimezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }).format(now);

    // Parse the timezone-specific times
    const storeCurrentParsed = new Date(
      storeNow.replace(
        /(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2}):(\d{2})/,
        '$1-$2-$3T$4:$5:$6'
      )
    );
    const userCurrentParsed = new Date(
      userNow.replace(
        /(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2}):(\d{2})/,
        '$1-$2-$3T$4:$5:$6'
      )
    );

    // Calculate the timezone offset difference in milliseconds
    const offsetDiff =
      userCurrentParsed.getTime() - storeCurrentParsed.getTime();

    // Apply the offset to our store time
    const convertedDateTime = new Date(storeDateTime.getTime() + offsetDiff);

    // Format in user's preferred format - ALWAYS use 12-hour AM/PM format with explicit locale
    const finalUserTime = new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      hourCycle: 'h12',
    }).format(convertedDateTime);

    if (includeTimezoneInfo) {
      return {
        originalTime: storeTime,
        convertedTime: finalUserTime,
        storeTimezone: storeTimezone,
        userTimezone: userTimezone,
        converted: convertedFlag,
      };
    }

    return finalUserTime;
  } catch (error) {
    console.error('❌ ERROR in convertStoreTimeToUserLocal:', error);
    return storeTime; // Return original time if conversion fails
  }
};

// Utility function to format time slots with timezone conversion
export const formatTimeSlotForUser = (timeSlot, storeTimezone) => {
  if (!timeSlot) {
    return timeSlot;
  }

  // Handle both range format (HH:MM - HH:MM) and single time format (HH:MM)
  if (timeSlot.includes(' - ')) {
    // Range format
    const [startTime, endTime] = timeSlot.split(' - ');

    const convertedStart = convertStoreTimeToUserLocal(
      startTime.trim(),
      storeTimezone
    );
    const convertedEnd = convertStoreTimeToUserLocal(
      endTime.trim(),
      storeTimezone
    );

    const result = `${convertedStart} - ${convertedEnd}`;
    return result;
  } else {
    // Single time format - convert and ensure AM/PM format
    const convertedTime = convertStoreTimeToUserLocal(
      timeSlot.trim(),
      storeTimezone
    );
    return convertedTime;
  }
};

// Utility function to show timezone information to user
export const getTimezoneDisplayInfo = (storeTimezone) => {
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const now = new Date();

  const storeTime = new Intl.DateTimeFormat('en-US', {
    timeZone: storeTimezone,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    hourCycle: 'h12',
    timeZoneName: 'short',
  }).format(now);

  const userTime = new Intl.DateTimeFormat('en-US', {
    timeZone: userTimezone,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    hourCycle: 'h12',
    timeZoneName: 'short',
  }).format(now);

  const result = {
    storeTime,
    userTime,
    storeTimezone,
    userTimezone,
    isDifferent: storeTimezone !== userTimezone,
  };

  return result;
};

// Utility function to convert user selected time back to UTC for backend
export const convertUserTimeToUTC = (
  userTime,
  userTimezone,
  selectedDate = new Date()
) => {
  try {
    // Parse the user time
    const [time, period] =
      userTime.includes('AM') || userTime.includes('PM')
        ? userTime.split(/\s+/)
        : [userTime, null];

    const [hours, minutes] = time.split(':').map((num) => parseInt(num));
    let adjustedHours = hours;

    // Handle 12-hour format
    if (period) {
      if (period.toUpperCase() === 'PM' && hours !== 12) {
        adjustedHours = hours + 12;
      } else if (period.toUpperCase() === 'AM' && hours === 12) {
        adjustedHours = 0;
      }
    }

    // SIMPLE AND RELIABLE APPROACH:
    // Create a date string that explicitly represents the time in user's timezone

    const year = selectedDate.getFullYear();
    const month = (selectedDate.getMonth() + 1).toString().padStart(2, '0');
    const day = selectedDate.getDate().toString().padStart(2, '0');
    const hour = adjustedHours.toString().padStart(2, '0');
    const minute = (minutes || 0).toString().padStart(2, '0');
    // Create ISO string: YYYY-MM-DDTHH:MM:SS
    const isoStringLocal = `${year}-${month}-${day}T${hour}:${minute}:00`;
    // Use a reference date to calculate the timezone offset
    const refDate = new Date();
    const refInUserTZ = new Date(
      refDate.toLocaleString('en-US', { timeZone: userTimezone })
    );
    // Calculate how many hours user timezone is ahead/behind UTC
    const offsetHours =
      (refDate.getTime() - refInUserTZ.getTime()) / (1000 * 60 * 60);
    // For Egypt (UTC+3): offsetHours should be around -3
    // Meaning Egypt time - 3 hours = UTC time
    const targetLocal = new Date(isoStringLocal);
    const targetUTC = new Date(
      targetLocal.getTime() + offsetHours * 60 * 60 * 1000
    );

    // Verification - convert back to user timezone to check
    const verification = new Intl.DateTimeFormat('en-US', {
      timeZone: userTimezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    }).format(targetUTC);

    return targetUTC;
  } catch (error) {
    console.error('❌ ERROR in convertUserTimeToUTC:', error);
    return selectedDate;
  }
};

export const validateUserAddress = async (token, address, setErr) => {
  try {
    const tokenId = await auth?.currentUser.getIdToken().then((token) => token);
    const headers = {
      'Content-Type': 'application/json',
      Authorization: tokenId,
    };
    fetch(`${environment?.apiUrl}/pickUpOrDelivery`, {
      method: 'POST',
      body: {
        ...address,
      },
      headers: headers,
    });
  } catch (e) {
    setErr(e.message);
  }
};
const get4ays = (data) => {
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const today = new Date().getDay();
  let yesterday = today === 0 ? 6 : today - 1;
  let tomo = today < 5 ? today + 1 : today == 5 ? 6 : 1;
  let after = today < 5 ? today + 2 : today === 5 ? 0 : 1;
  const wanted = [days[yesterday], days[today], days[tomo], days[after]];
  let times = wanted.map((day) => {
    return data.filter((item) => item.day == day)[0] || [];
  });
  return times;
};
// getTimeZones function
export const getTimeZones = async (restId, onFail) => {
  // Add validation to prevent Firebase error
  if (!restId || restId.trim() === '') {
    console.error('getTimeZones called with empty restId');
    if (onFail) onFail('Store ID is required');
    return null;
  }

  try {
    const res = await firestore.collection('stores').doc(restId).get();

    if (!res.exists) {
      console.error('Store not found for ID:', restId);
      if (onFail) onFail('Store not found');
      return null;
    }

    if (res.data().endTime) {
      const endtime = new Date(
        res.data().endTime.seconds * 1000 + res.data().endTime.nanoseconds / 1e6
      );

      if (endtime > new Date()) return false;
    }

    const timing = get4ays(res.data().timing);
    const times = {};
    const todayData = timing[1]?.day;
    const tomorrowData = timing[2]?.day;
    const afterTomorrowData = timing[3]?.day;

    // extract times from all date - ensure 12-hour AM/PM format
    const options = { hour: 'numeric', minute: '2-digit', hour12: true, hourCycle: 'h12' };
    const convertedTimes = timing.map((item) => {
      const slots = item?.slots?.map((slot) => {
        const close_time = slot.close_time
          .toDate()
          .toLocaleTimeString('en-US', options);
        const open_time = slot.open_time
          .toDate()
          .toLocaleTimeString('en-US', options);
        return { ...slot, open_time, close_time };
      });
      return { ...item, slots };
    });
    //-------
    // Calculate current availability for today
    times.today = todayData
      ? iterateTimeForToday(convertedTimes[1]?.slots)
      : [];
    times.tomorrow = tomorrowData ? iterateTime(convertedTimes[2]?.slots) : [];
    times.afterTomorrow =
      timing[3] && afterTomorrowData
        ? iterateTime(convertedTimes[3]?.slots)
        : [];
    times.asap = asap_Is_Available(convertedTimes[1]?.slots);
    return times;
  } catch (error) {
    console.error('Error in getTimeZones:', error);
    if (onFail) onFail('Failed to fetch store information');
    return null;
  }
};

const asap_Is_Available = (value) => {
  const now = new Date();

  if (value !== null && value !== undefined) {
    for (const slot of value) {
      const startTime = new Date(now.toDateString() + ' ' + slot?.open_time);
      const endTime = new Date(now.toDateString() + ' ' + slot?.close_time);

      if (now >= startTime && now <= endTime) {
        return true; // Early return when a valid slot is found
      }
    }
  } else {
    console.log('Value is null or undefined');
  }

  return false;
};

// end getTimeZones function

// Sample iterateTime function (updated to handle your data structure)
export const iterateTime = (data) => {
  if (!data || !Array.isArray(data)) return [];
  return data.reduce((arr, slot) => {
    const currentDate = new Date(); // Get the current date
    const openTime = new Date(
      currentDate.toDateString() + ' ' + slot.open_time
    );
    const closeTime = new Date(
      currentDate.toDateString() + ' ' + slot.close_time
    );
    if (!isNaN(openTime) && !isNaN(closeTime)) {
      while (openTime < closeTime) {
        const nextTime = new Date(openTime.getTime() + 60 * 60 * 1000);
        const timeRange = `${formatTime(openTime)} - ${formatTime(nextTime)}`;
        arr.push({ time: timeRange, timezone: slot.timezone });
        openTime.setTime(nextTime.getTime());
      }
    }
    return arr;
  }, []);
};
//iteration time for today
export const iterateTimeForToday = (data) => {
  if (!data || !Array.isArray(data)) return [];
  const now = new Date();
  now.setHours(now.getHours() + 1); // Add 1 hour to now
  const arr = [];
  data.forEach((slot) => {
    const currentDate = new Date(); // Get the current date
    const openTime = new Date(
      currentDate.toDateString() + ' ' + slot.open_time
    );
    const closeTime = new Date(
      currentDate.toDateString() + ' ' + slot.close_time
    );
    if (!isNaN(openTime) && !isNaN(closeTime)) {
      // Use a plain for loop to iterate through the time slots
      for (
        let time = new Date(openTime);
        time < closeTime;
        time.setHours(time.getHours() + 1)
      ) {
        const nextTime = new Date(time.getTime() + 60 * 60 * 1000);
        if (nextTime <= now) {
          continue; // Skip past time slots that are in the past
        }
        const timeRange = `${formatTime(time)} - ${formatTime(nextTime)}`;
        arr.push({ time: timeRange, timezone: slot.timezone });
      }
    }
  });
  return arr;
};

// -----

const formatTime = (date) => {
  return new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    hourCycle: 'h12',
  }).format(date);
};

export function getOffsetForLoc(loc, date = new Date()) {
  let opts = {
    hour: 'numeric',
    timeZone: 'America/Chicago',
    timeZoneName: 'short',
  };
  let getOffset = (lang) =>
    new Intl.DateTimeFormat(lang, opts)
      .formatToParts(date)
      .reduce((acc, part) => {
        acc[part.type] = part.value;
        return acc;
      }, {}).timeZoneName;
  let offset = getOffset('en');
  // If offset is an abbreviation, change language
  if (!/^UCT|GMT/.test(offset)) {
    offset = getOffset('fr');
  }
  // Remove GMT/UTC
  let res = offset.substring(3);
  if (res[0] == '−') res = -+res.replace('−', '');

  return +res;
}

export const getTomorrowScheduleLater = () => {
  // Get current date
  var today = new Date();
  // Add one day to the current date
  var tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  // Get the month and day components of the tomorrow's date
  var month = tomorrow.getMonth() + 1; // Months are zero-based
  var day = tomorrow.getDate() + 1;
  // Format the date as "mm/d"
  var formattedDate = month.toString().padStart(2, '0') + '/' + day;

  return formattedDate; // Output: "06/6" (for June 6th)
};
export const getTomorrow = () => {
  const Englishdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const Arabicdays = [
    'الأحد',
    'الاثنين',
    'الثلاثاء',
    'الأربعاء',
    'الخميس',
    'الجمعة',
    'السبت',
  ];

  const tomorrow = new Date();
  const afterTo = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  afterTo.setDate(afterTo.getDate() + 2);
  const tomorrowData = {
    date: tomorrow.getDate(),
    day: Englishdays[tomorrow.getDay()],
    dayInArabic: Arabicdays[tomorrow.getDay()],
    month: tomorrow.getMonth(),
  };
  const afterData = {
    date: afterTo.getDate(),
    day: Englishdays[afterTo.getDay()],
    dayInArabic: Arabicdays[afterTo.getDay()],
    month: afterTo.getMonth(),
  };
  return { tomorrow: tomorrowData, after: afterData };
};

export function startsWithPlus(phoneNumber, countryCode) {
  let regex;
  if (countryCode === '+1') {
    regex = /^\+1\d{10}$/;
  } else if (countryCode === '+962') {
    regex = /^\+962\d{9}$/;
  } else if (countryCode === '+20') {
    regex = /^\+20\d{10}$/;
  } else {
    throw new Error('Unsupported country code');
  }
  return regex.test(phoneNumber);
}
